import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLang } from '../context/LangContext';
import { getTeamMembers } from '../data/teamData';

// 导入doctor和nurse图片
import doctor1 from '../assets/images/employees/group1.png';
// 导入厂商logo图片
import allerganLogo from '../assets/images/brand_logo/allergan.png';
import clarionLogo from '../assets/images/brand_logo/clarion.png';
import cynosureLogo from '../assets/images/brand_logo/Cynosure.jpg';
import galdermaLogo from '../assets/images/brand_logo/galderma.jpg';
import hydrafacialLogo from '../assets/images/brand_logo/hydrafacial.jpg';
import merzLogo from '../assets/images/brand_logo/merz.jpg';
import scitonLogo from '../assets/images/brand_logo/sciton.jpg';
import skinceuticalsLogo from '../assets/images/brand_logo/skinceuticals.png';

const content = {
  en: {
    pageTitle: 'About Us',
    metaTitle: 'About Our Medical Team & Clinic | Halo Aesthetic Clinic Richmond Hill',
    pageDesc: 'We are committed to providing every client with professional, safe, and personalized medical aesthetics services.',
    metaDesc: 'Meet our licensed medical practitioners, nurses, and skin specialists at Halo Aesthetic Clinic in Richmond Hill. Learn about our clinical safety standards, treatment philosophy, and advanced technology.',
    storyTitle: 'Our Story',
    story1: 'At Halo Aesthetic Clinic, we have brought together a team of passionate professionals united by a shared vision: to redefine the medical aesthetic experience through masterful skill, uncompromising standards, and a deep appreciation for natural beauty.',
    story2: 'Each member of our team contributes unique expertise—ranging from sharp business insight and exceptional leadership to refined clinical techniques and artistic vision—all dedicated to providing you with personalized care that goes above and beyond expectations.',
    expYears: '8+',
    expYearsDesc: 'Years of Experience',
    doctors: '10+',
    doctorsDesc: 'Professional Experts',
    clients: '5000+',
    clientsDesc: 'Satisfied Clients',
    teamTitle: 'Our Medical Team',
    certTitle: 'Authorized Medical Aesthetic Partners',
    certs: [
      { id: 1, name: 'Allergan', image: allerganLogo },
      { id: 2, name: 'Clarion', image: clarionLogo },
      { id: 3, name: 'Cynosure', image: cynosureLogo },
      { id: 4, name: 'Galderma', image: galdermaLogo },
      { id: 5, name: 'HydraFacial', image: hydrafacialLogo },
      { id: 6, name: 'Merz', image: merzLogo },
      { id: 7, name: 'Sciton', image: scitonLogo },
      { id: 8, name: 'SkinCeuticals', image: skinceuticalsLogo },
    ],
  },
  cn: {
    pageTitle: '关于我们',
    metaTitle: '关于我们的医疗团队与诊所 | Halo Richmond Hill 医美诊所',
    pageDesc: '我们致力于为每位客户提供专业、安全、个性化的医美服务',
    metaDesc: '了解 Halo 医美诊所 Richmond Hill 的持牌医疗专家团队、专业资质、诊所服务理念及高标准医疗安全规范。',
    storyTitle: '我们的故事',
    story1: '在Halo医美中心，我们聚集了一群满怀热情的专业人士，共同追寻一个愿景：以精湛技艺、严谨标准和对自然美的追求，重新定义医美体验。',
    story2: ' 我们的团队各具专长，无论是敏锐的行业洞察、卓越的管理能力，还是精湛的临床技术与艺术审美，都致力于为您带来超越期待的个性化服务。',
    expYears: '8+',
    expYearsDesc: '年行业经验',
    doctors: '10+',
    doctorsDesc: '专业技术',
    clients: '5000+',
    clientsDesc: '满意客户',
    teamTitle: '医疗专家团队',
    certTitle: '认证合作厂商',
    certs: [
      { id: 1, name: 'Allergan', image: allerganLogo },
      { id: 2, name: 'Clarion', image: clarionLogo },
      { id: 3, name: 'Cynosure', image: cynosureLogo },
      { id: 4, name: 'Galderma', image: galdermaLogo },
      { id: 5, name: 'HydraFacial', image: hydrafacialLogo },
      { id: 6, name: 'Merz', image: merzLogo },
      { id: 7, name: 'Sciton', image: scitonLogo },
      { id: 8, name: 'SkinCeuticals', image: skinceuticalsLogo },
    ],
  }
};

const About = () => {
  const { lang } = useLang();
  const location = useLocation();
  const c = content[lang];
  const teamList = getTeamMembers(lang);

  const [activeHash, setActiveHash] = useState('');

  useEffect(() => {
    const hash = location.hash ? location.hash.replace('#', '') : '';
    setActiveHash(hash);
    if (hash) {
      const el = document.getElementById(hash);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 150);
      }
    }
  }, [location.hash]);

  const canonicalUrl = 'https://www.haloaestheticclinic.com/about';

  return (
    <div className="bg-softGradient bg-no-repeat bg-top text-text">
      <Helmet>
        <title>{c.metaTitle}</title>
        <meta name="description" content={c.metaDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={c.metaTitle} />
        <meta property="og:description" content={c.metaDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* 品牌介绍 */}
      <section className="py-section max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 text-gray-900">{c.pageTitle}</h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">{c.pageDesc}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="overflow-hidden rounded-xl">
            <img
              src={doctor1}
              alt="clinic"
              className="w-full h-96 object-cover border border-champagne/40 transition-transform duration-500 hover:scale-105"
            />
          </div>
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-[#2E6B4C]">{c.storyTitle}</h2>
            <p className="text-gray-700">{c.story1}</p>
            <p className="text-gray-700">{c.story2}</p>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-champagne/40">
                <div className="text-3xl font-bold text-[#2E6B4C] mb-2">{c.expYears}</div>
                <div className="text-gray-700">{c.expYearsDesc}</div>
              </div>
              <div className="text-center bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-champagne/40">
                <div className="text-3xl font-bold text-[#2E6B4C] mb-2">{c.doctors}</div>
                <div className="text-gray-700">{c.doctorsDesc}</div>
              </div>
              <div className="text-center bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-champagne/40">
                <div className="text-3xl font-bold text-[#2E6B4C] mb-2">{c.clients}</div>
                <div className="text-gray-700">{c.clientsDesc}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 专家团队 */}
      <section className="py-section bg-softGradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">{c.teamTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {teamList.map((member) => (
              <div
                key={member.id}
                id={member.slug}
                className={`bg-white/80 backdrop-blur-sm rounded-xl shadow-card overflow-hidden border transition-all duration-500 scroll-mt-28 ${
                  activeHash === member.slug
                    ? 'border-[#2E6B4C] ring-2 ring-[#2E6B4C]/60 shadow-glow scale-[1.02]'
                    : 'border-champagne/40 hover:shadow-glow'
                }`}
              >
                {/* 统一比例容器：3:4 竖图 */}
                <div className="w-full aspect-[3/4] bg-gray-100/80 flex items-center justify-center overflow-hidden">
                  <img
                    src={member.avatar}
                    alt={member.name}
                    className="w-full h-full object-contain transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-xl font-bold text-gray-900">{member.name}</h3>
                    {(member.isLicensedMedical || member.licenseNumber) && (
                      <span className="text-xs font-semibold text-[#2E6B4C] bg-[#2E6B4C]/10 px-2 py-0.5 rounded">
                        Licensed Medical
                      </span>
                    )}
                  </div>
                  <p className="text-[#2E6B4C] mb-3 font-medium text-sm">{member.title}</p>
                  <p className="text-gray-700 text-sm leading-relaxed mb-4">{member.bio}</p>

                  {member.specialties && member.specialties.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-3 border-t border-champagne/30">
                      {member.specialties.map((spec, i) => (
                        <span
                          key={i}
                          className="text-xs bg-[#2E6B4C]/10 border border-[#2E6B4C]/20 text-[#2E6B4C] font-medium px-2.5 py-0.5 rounded-full"
                        >
                          #{spec}
                        </span>
                      ))}
                    </div>
                  )}

                  {member.hasDetailPage && (
                    <div className="pt-4 mt-4 border-t border-champagne/30 flex items-center justify-between">
                      <Link
                        to={`/team/${member.slug}`}
                        className="inline-flex items-center text-xs font-bold text-[#2E6B4C] hover:text-[#23533a] group"
                      >
                        <span>{lang === 'cn' ? '查看个人主页与权威资质' : 'View Full Profile & Credentials'}</span>
                        <span className="ml-1 transition-transform group-hover:translate-x-1">→</span>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 荣誉资质 - 合作厂商认证 */}
      <section className="py-section bg-softRadial">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">{c.certTitle}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
            {c.certs.map((cert) => (
              <div
                key={cert.id}
                className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-champagne/40 flex items-center justify-center transition-all duration-300 hover:shadow-lg hover:scale-105"
              >
                <div className="w-full h-32 flex items-center justify-center overflow-hidden">
                  <img
                    src={cert.image}
                    alt={cert.name}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About; 